import * as React from "react";
import { observer } from "mobx-react";
import { CampaignQueryResult, DealsProps, DealsFlexModuleResult } from "./typings";
import { withStores } from "stores";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { useLocalization } from "@shared-ui/localization-context";
import { DealsPlaceholder } from "./DealsPlaceHolder";
import { GridPlaceholder } from "./GridPlaceHolder";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { AuthenticationState } from "bernie-context";
import { CampaignByIdQuery } from "../../../../__generated__/api";
import { MerchCampaignCard } from "components/shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { DealsErrorFallback } from "./DealsErrorFallback";
import { DealsDebugInfo } from "components/flexComponents/Deals/DealsDebugInfo";
import Campaigns = CampaignByIdQuery.Campaigns;
import Offers = CampaignByIdQuery.Offers;
import { locKeys } from "components/flexComponents/MerchCampaign/l10n";
import { UitkSecondaryButton } from "uitk-react-button";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLoader } from "uitk-react-loader";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { UitkSpacing } from "uitk-react-spacing";

const Deals = withStores(
  "routeStore",
  "dealsStore",
  "flexViewModel",
  "flexModuleModelStore"
)(
  observer((props: DealsProps) => {
    const { flexTemplateNode, flexViewModel, context, flexModuleModelStore } = props;
    const { id, options } = flexTemplateNode;
    const { pathname, search } = props.routeStore;

    const model = flexModuleModelStore.getModel(id) as DealsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { formatText } = useLocalization();
    const keyHelper = new ItemKeyHelper(id);
    const campaignId = model?.campaignId;
    const returnPath = `${pathname}${search}`;
    const { lat, long, debug, origin, referrer } = props.routeStore.queryParams;
    const showMoreButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();
    const { userLocation } = flexViewModel;
    const [campaignQueryResult, setCampaignQueryResult] = React.useState<CampaignQueryResult>();
    const [campaign, setCampaign] = React.useState<Campaigns>();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [campaignFetchFailed, setCampaignFetchFailed] = React.useState<boolean>(false);
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);
    const [paginationReferrer, setPaginationReferrer] = React.useState<string>();

    const track = useClickTracker();
    const showMoreTrack = (moduleName: string) => {
      const flexTracking: FlexTrackingInfo = {
        moduleName,
        rfrr: `${flexViewModel.trackingContext?.pageName}.offers.pagination`,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTracking, track);
    };

    const {
      destinationView,
      displayDate,
      displayDescription,
      displayPrice,
      displayPropertyCount,
      enableLivePricing,
    } = model;

    React.useEffect(() => {
      fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
      showMoreButtonRef.current?.blur();
      if (shouldRender()) {
        setIsLoadingNextPage(true);
        props.dealsStore
          .fetchCampaignDeals(
            props.context,
            campaignId,
            model.enableTripAttach,
            pageNumber,
            model.campaignContentPagination ? model.campaignContentPageSize : null || undefined,
            lat ? lat : userLocation?.latitude,
            long ? long : userLocation?.longitude,
            origin,
            model.displayPropertyCount,
            flexViewModel.trackingContext?.pageName,
            paginationReferrer ? paginationReferrer : referrer,
            enableLivePricing,
            returnPath
          )
          .then((result) => {
            if (campaign) {
              campaign.offers = campaign.offers?.concat(result.campaign?.offers || []);

              if (campaign.campaignContentPaginationInfo && result.campaign?.campaignContentPaginationInfo) {
                const hasMoreContentPages = result.campaign?.campaignContentPaginationInfo?.hasMoreContentPages;
                campaign.campaignContentPaginationInfo.hasMoreContentPages = hasMoreContentPages;
              }
              setCampaign(campaign);
            } else {
              setCampaign(result.campaign);
            }
            setCampaignQueryResult(result);
            setIsLoadingNextPage(false);
            setIsLoading(false);
            setCampaignFetchFailed(result === null);
            setPageNumber(pageNumber + 1);
            setPaginationReferrer(result.paginationReferrer);
          });
      }
    };

    const openDialog = (dialogPath: string) => {
      props.routeStore.updateSearch({ pwaDialog: dialogPath });
    };

    const closeDialog = () => {
      props.routeStore.closeDialog("pwaDialog");
    };

    const shouldRender = () => {
      return (
        campaignId &&
        (!model.enableTripAttach ||
          (model.enableTripAttach && context.user.authState === AuthenticationState.AUTHENTICATED))
      );
    };

    if (!shouldRender()) {
      return null;
    }

    const showErrorFallBack = () => {
      return model.isFeatured && (campaignFetchFailed || !hasEnoughDeals());
    };

    const hasEnoughDeals = () => {
      return campaign?.offers?.length && campaign?.offers?.length >= 4;
    };

    const renderMerchCampaignCard = (c: Campaigns, o: Offers, index: number) => {
      return (
        <MerchCampaignCard
          offer={o}
          cardIndex={index}
          campaignId={c.id}
          productTypes={c.productTypes}
          recommendedType={c.recommendedType}
          openDialog={openDialog}
          closeDialog={closeDialog}
          destinationView={destinationView}
          displayDate={displayDate}
          displayDescription={displayDescription}
          displayPrice={displayPrice}
          displayPropertyCount={displayPropertyCount}
          border={true}
          moduleName="Deals"
        />
      );
    };

    const isGrid = model.view === "grid";

    return isLoading ? (
      isGrid ? (
        <GridPlaceholder flexTemplateNodeId={id} />
      ) : (
        <DealsPlaceholder flexTemplateNodeId={id} />
      )
    ) : (
      <>
        {debug === "true" && <DealsDebugInfo queryResult={campaignQueryResult} campaignId={campaignId} />}
        {showErrorFallBack() && <DealsErrorFallback />}

        {campaign && hasEnoughDeals() && (
          <UitkSpacing margin={{ blockend: "six" }}>
            <div data-testid="deals" className="Deals" id={id} data-fm={options.fmId}>
              <UitkSpacing margin={{ blockstart: "six" }}>
                <div data-testid="deals-carousel">
                  <CampaignHeader
                    context={props.context}
                    campaign={campaign}
                    openDialog={openDialog}
                    closeDialog={closeDialog}
                    campaignTitle={model.title}
                    campaignCopyAlignment={model.campaignCopyAlignment}
                    showCampaignDescription={model.showCampaignDescription}
                    showCampaignTitle={model.showCampaignTitle}
                    showCampaignTermsAndConditions={model.showTermsAndConditions}
                    view={model.view}
                    imgSrc={model.heroImageSrc}
                  />
                  {model.view === "carousel-vr" && (
                    <Viewport>
                      <ViewSmall>
                        <UitkSpacing margin={{ blockstart: "three" }}>
                          <IsomorphicCarousel
                            className="carouselComponent"
                            itemsVisible={{
                              lg: 2,
                              md: 2,
                              sm: 2,
                            }}
                            buttonText={{
                              nextButton: formatText("carousel.item.next"),
                              prevButton: formatText("carousel.item.prev"),
                            }}
                            pageBy={1}
                            peekWidth={0}
                          >
                            {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                              return (
                                <div className="uitk-height-item" key={keyHelper.next()}>
                                  {renderMerchCampaignCard(campaign, offer, cardIndex)}
                                </div>
                              );
                            }) || []}
                          </IsomorphicCarousel>
                        </UitkSpacing>
                      </ViewSmall>
                      <ViewMedium>
                        <UitkSpacing padding={{ inline: "twelve" }}>
                          <UitkLayoutGrid columns={4} space="three">
                            {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                              return (
                                <div className="uitk-height-item" key={keyHelper.next()} data-testid="deals-grid">
                                  {renderMerchCampaignCard(campaign, offer, cardIndex)}
                                </div>
                              );
                            }) || []}
                          </UitkLayoutGrid>
                        </UitkSpacing>
                      </ViewMedium>
                    </Viewport>
                  )}
                  {model.view === "carousel-redesign" && (
                    <UitkSpacing margin={{ blockstart: "three" }}>
                      <IsomorphicCarousel
                        className="carouselComponent"
                        itemsVisible={{
                          lg: 4,
                          md: 4,
                          sm: 2,
                        }}
                        buttonText={{
                          nextButton: formatText("carousel.item.next"),
                          prevButton: formatText("carousel.item.prev"),
                        }}
                        pageBy={1}
                        peekWidth={0}
                      >
                        {campaign.offers?.map((offer, cardIndex) => {
                          return (
                            <div className="uitk-height-item" key={keyHelper.next()}>
                              {renderMerchCampaignCard(campaign, offer, cardIndex)}
                            </div>
                          );
                        }) || []}
                      </IsomorphicCarousel>
                    </UitkSpacing>
                  )}
                  {(model.view === "carousel" || model.view == null) && (
                    <UitkSpacing margin={{ blockstart: "three" }}>
                      <IsomorphicCarousel
                        className="carouselComponent"
                        itemsVisible={{
                          lg: 3,
                          md: 2,
                          sm: 1,
                        }}
                        buttonText={{
                          nextButton: formatText("carousel.item.next"),
                          prevButton: formatText("carousel.item.prev"),
                        }}
                        pageBy={1}
                        peekWidth={24.8}
                      >
                        {campaign.offers?.map((offer, cardIndex) => {
                          return (
                            <div className="uitk-height-item" key={keyHelper.next()}>
                              {renderMerchCampaignCard(campaign, offer, cardIndex)}
                            </div>
                          );
                        }) || []}
                      </IsomorphicCarousel>
                    </UitkSpacing>
                  )}
                  {isGrid && (
                    <UitkSpacing margin={{ blockstart: "three", inlinestart: "three", inlineend: "three" }}>
                      <UitkLayoutGrid columns={{ medium: 2, extra_large: 3, small: 1 }} space="three">
                        {campaign.offers?.map((offer, cardIndex) => {
                          return (
                            <div className="uitk-height-item" key={keyHelper.next()} data-testid="deals-grid">
                              {renderMerchCampaignCard(campaign, offer, cardIndex)}
                            </div>
                          );
                        }) || []}
                      </UitkLayoutGrid>
                    </UitkSpacing>
                  )}
                </div>
              </UitkSpacing>
              {model.campaignContentPagination &&
                isGrid &&
                campaign.campaignContentPaginationInfo?.hasMoreContentPages && (
                  <UitkSpacing padding={{ blockstart: "six", blockend: "six" }}>
                    <div className="uitk-type-center">
                      <UitkSecondaryButton
                        name="showMoreButton"
                        type="button"
                        onClick={fetchCampaigns}
                        text={formatText(locKeys.showMore)}
                        domRef={showMoreButtonRef}
                        analytics={{
                          id: "merch.campaign",
                          callback: showMoreTrack,
                        }}
                      />
                    </div>
                  </UitkSpacing>
                )}
              {isLoadingNextPage && (
                <UitkLayoutFlex justifyContent="center">
                  <UitkSpacing margin={{ blockstart: "four" }}>
                    <UitkLayoutFlexItem>
                      <div>
                        <UitkLoader />
                      </div>
                    </UitkLayoutFlexItem>
                  </UitkSpacing>
                </UitkLayoutFlex>
              )}
            </div>
          </UitkSpacing>
        )}
      </>
    );
  })
);

export default Deals;
